'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { useState } from 'react';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './auth-provider';
import { I18nProvider } from '@/i18n';
import { LocalePickerModal } from '@/components/locale-picker-modal';
import { PreviewModeProvider } from './preview-mode-provider';

/**
 * Reads the authenticated user's language preference and passes it to I18nProvider.
 * Also renders the LocalePickerModal when the user has no language set yet (first login).
 * Must be rendered inside AuthProvider.
 */
function I18nBridge({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  // user.language is stored as the short code (e.g. "ro", "en", "bg", "el", "hu").
  // Map it to a tenant locale string so I18nProvider can normalise it.
  // Show locale modal when user is logged in but has no language selected yet.
  const hasLocalLocale = typeof window !== 'undefined' && !!localStorage.getItem('crm_locale');
  const showLocalePicker =
    !isLoading &&
    user !== null &&
    (user.language === null || user.language === undefined) &&
    !hasLocalLocale;
  return (
    <I18nProvider tenantLocale={user?.language ?? undefined}>
      {children}
      {showLocalePicker && <LocalePickerModal />}
    </I18nProvider>
  );
}

export function Providers({ children, nonce }: { children: React.ReactNode; nonce?: string }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // staleTime=0 with the default `refetchOnMount: true` gives us a
            // stale-while-revalidate pattern: the cache from gcTime is shown
            // instantly with no spinner, while a background refetch picks up
            // any changes (e.g. a tenant created on /settings/tenants/new
            // appears the moment the user navigates back to the list).
            // Network cost is bounded by gcTime — only mounted queries refetch.
            staleTime: 0,
            gcTime: 5 * 60_000, // 5 minutes — keep cache around for instant paint
            refetchOnWindowFocus: false, // avoid refetch storms on tab switch
            // Only retry on network errors or 5xx. Never retry 4xx (including 429 Too Many
            // Requests) — retrying client errors immediately makes rate-limit cascades worse.
            retry: (failureCount, error: unknown) => {
              const status = (error as { response?: { status?: number } })?.response?.status;
              if (status && status >= 400 && status < 500) return false;
              return failureCount < 1;
            },
          },
          mutations: {
            retry: 0, // mutations should not auto-retry (could double-submit)
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem nonce={nonce}>
        <PreviewModeProvider>
          <AuthProvider>
            <I18nBridge>
              {children}
              <Toaster position="top-right" richColors />
            </I18nBridge>
          </AuthProvider>
        </PreviewModeProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
